/**
 * AppVersionPill (iter316r · 2026-02-23) — bundle-hash badge + cache
 * bust nudge.
 *
 * Two jobs:
 *   1. Show the current `main.<hash>.js` short hash as a fixed
 *      bottom-right pill on every /account/* page so support can ask
 *      vendors reporting "UI vanished" whether they're on the latest
 *      deploy. Click → copies the full filename to clipboard.
 *   2. Detect stale bundles. On every `visibilitychange` returning
 *      the tab to the foreground, re-fetch `/` (index.html) with
 *      cache-buster, parse the CURRENTLY-SERVED main hash, and if it
 *      differs from what the browser is running, expand the pill
 *      into a "🔄 Reload to update" button so the vendor can bring
 *      themselves current in one tap.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const HASH_RE = /main\.([a-f0-9]+)\.js/;

function readCurrentBundleHash() {
  try {
    const scripts = document.querySelectorAll('script[src*="/static/js/main."]');
    for (const s of scripts) {
      const m = (s.src || "").match(HASH_RE);
      if (m) return m[1];
    }
  } catch (e) { /* noop */ }
  return "";
}

export default function AppVersionPill() {
  const [runtimeHash, setRuntimeHash] = useState("");
  const [servedHash, setServedHash]   = useState("");
  const [copied, setCopied]           = useState(false);
  const inflight = useRef(false);

  useEffect(() => {
    const h = readCurrentBundleHash();
    setRuntimeHash(h);
    setServedHash(h);   // baseline
  }, []);

  const checkServed = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const r = await fetch(`/?_hp=${Date.now()}`, {
        credentials: "same-origin",
        cache: "no-store",
        headers: { "Cache-Control": "no-cache" },
      });
      if (!r.ok) return;
      const html = await r.text();
      const m = html.match(HASH_RE);
      if (m && m[1]) setServedHash(m[1]);
    } catch (e) { /* offline / CORS — ignore */ }
    finally { inflight.current = false; }
  }, []);

  useEffect(() => {
    if (!runtimeHash) return undefined;
    const onVis = () => {
      if (document.visibilityState === "visible") checkServed();
    };
    document.addEventListener("visibilitychange", onVis);
    // Also poll every 5 min in case the tab stays foreground.
    const id = setInterval(checkServed, 5 * 60 * 1000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(id);
    };
  }, [runtimeHash, checkServed]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`main.${runtimeHash}.js`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* clipboard denied */ }
  };

  if (!runtimeHash) return null;

  const stale = servedHash && servedHash !== runtimeHash;
  if (stale) {
    return (
      <button data-testid="app-version-pill-reload"
              onClick={() => window.location.reload()}
              title={`New version available (main.${servedHash}.js). You're on main.${runtimeHash}.js. Click to reload.`}
              style={{
                position: "fixed", bottom: 8, right: 8, zIndex: 9998,
                fontSize: 11, padding: "6px 12px", borderRadius: 999,
                background: "#0b1220", color: "#fde68a",
                fontWeight: 700, letterSpacing: 0.3,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                cursor: "pointer", userSelect: "none", border: "1.5px solid #f59e0b",
                boxShadow: "0 4px 12px rgba(245,158,11,0.35)",
                animation: "hp-pill-pulse 2s ease-in-out infinite",
              }}>
        🔄 Reload to update ({servedHash.slice(0, 8)})
        <style>{`@keyframes hp-pill-pulse {0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}`}</style>
      </button>
    );
  }

  return (
    <div data-testid="app-version-pill"
         onClick={copy}
         title={`Bundle main.${runtimeHash}.js — if a teammate sees a different hash, hard-refresh (Shift-Cmd-R or Ctrl-F5). Click to copy.`}
         style={{
           position: "fixed", bottom: 8, right: 8, zIndex: 9998,
           fontSize: 10, padding: "3px 8px", borderRadius: 999,
           background: "rgba(15,23,42,0.72)", color: "#e2e8f0",
           fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
           letterSpacing: 0.5, cursor: "pointer", userSelect: "none",
           boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
         }}>
      {copied ? "copied" : `v.${runtimeHash.slice(0, 8)}`}
    </div>
  );
}
